function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe",
    ),
  );
}

class SectionId {
  static #separator = "__";

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(
    oldNode,
    newContent,
    preProcessCallbacks = [],
    postProcessCallbacks = [],
  ) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement("div");
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll("[id], [form]").forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form &&
        element.setAttribute(
          "form",
          `${element.form.getAttribute("id")}-${uniqueKey}`,
        );
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = "none";

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    const scripts = element.querySelectorAll("script[type='text/javascript']");
    scripts.forEach((oldScriptTag) => {
      const newScriptTag = document.createElement("script");
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute("role", "button");
  summary.setAttribute(
    "aria-expanded",
    summary.parentNode.hasAttribute("open"),
  );

  if (summary.nextElementSibling.getAttribute("id")) {
    summary.setAttribute("aria-controls", summary.nextElementSibling.id);
  }

  summary.addEventListener("click", (event) => {
    event.currentTarget.setAttribute(
      "aria-expanded",
      !event.currentTarget.closest("details").hasAttribute("open"),
    );
  });

  if (summary.closest("header-drawer, menu-drawer")) return;
  summary.parentElement.addEventListener("keyup", onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (
      event.target !== container &&
      event.target !== last &&
      event.target !== first
    )
      return;

    document.addEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== "TAB") return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if (
      (event.target === container || event.target === first) &&
      event.shiftKey
    ) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener("focusout", trapFocusHandlers.focusout);
  document.addEventListener("focusin", trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === "INPUT" &&
    ["search", "text", "email", "url"].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(":focus-visible");
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    "ARROWUP",
    "ARROWDOWN",
    "ARROWLEFT",
    "ARROWRIGHT",
    "TAB",
    "ENTER",
    "SPACE",
    "ESCAPE",
    "HOME",
    "END",
    "PAGEUP",
    "PAGEDOWN",
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener("keydown", (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener("mousedown", (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    "focus",
    () => {
      if (currentFocusedElement)
        currentFocusedElement.classList.remove("focused");

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add("focused");
    },
    true,
  );
}

function pauseAllMedia() {
  document.querySelectorAll(".js-youtube").forEach((video) => {
    video.contentWindow.postMessage(
      '{"event":"command","func":"' + "pauseVideo" + '","args":""}',
      "*",
    );
  });
  document.querySelectorAll(".js-vimeo").forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', "*");
  });
  document.querySelectorAll("video").forEach((video) => video.pause());
  document.querySelectorAll("product-model").forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener("focusin", trapFocusHandlers.focusin);
  document.removeEventListener("focusout", trapFocusHandlers.focusout);
  document.removeEventListener("keydown", trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== "ESCAPE") return;

  const openDetailsElement = event.target.closest("details[open]");
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector("summary");
  openDetailsElement.removeAttribute("open");
  summaryElement.setAttribute("aria-expanded", false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector("input");
    this.changeEvent = new Event("change", { bubbles: true });
    this.input.addEventListener("change", this.onInputChange.bind(this));
    this.querySelectorAll("button").forEach((button) =>
      button.addEventListener("click", this.onButtonClick.bind(this)),
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.quantityUpdate,
      this.validateQtyRules.bind(this),
    );
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === "plus") {
      // Enforce max boundary – prevent adding beyond available stock
      if (
        this.input.max &&
        parseInt(this.input.value) >= parseInt(this.input.max)
      )
        return;

      if (
        parseInt(this.input.dataset.min) > parseInt(this.input.step) &&
        this.input.value == 0
      ) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value)
      this.input.dispatchEvent(this.changeEvent);

    if (
      this.input.dataset.min === previousValue &&
      event.target.name === "minus"
    ) {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      const atMin = parseInt(value) <= parseInt(this.input.min);
      buttonMinus.classList.toggle("disabled", atMin);
      buttonMinus.disabled = atMin;
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      const atMax = value >= max;
      buttonPlus.classList.toggle("disabled", atMax);
      buttonPlus.disabled = atMax;
    }
  }
}

customElements.define("quantity-input", QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = "json") {
  return {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: `application/${type}`,
    },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == "undefined") {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent("on" + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options["method"] || "post";
  var params = options["parameters"] || {};

  var form = document.createElement("form");
  form.setAttribute("method", method);
  form.setAttribute("action", path);

  for (var key in params) {
    var hiddenField = document.createElement("input");
    hiddenField.setAttribute("type", "hidden");
    hiddenField.setAttribute("name", key);
    hiddenField.setAttribute("value", params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (
  country_domid,
  province_domid,
  options,
) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(
    options["hideElement"] || province_domid,
  );

  Shopify.addListener(
    this.countryEl,
    "change",
    Shopify.bind(this.countryHandler, this),
  );

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute("data-default");
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute("data-default");
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute("data-provinces");
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = "none";
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement("option");
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = "";
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement("option");
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector("details");

    this.addEventListener("keyup", this.onKeyUp.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll("summary").forEach((summary) =>
      summary.addEventListener("click", this.onSummaryClick.bind(this)),
    );
    this.querySelectorAll(
      "button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)",
    ).forEach((button) =>
      button.addEventListener("click", this.onCloseButtonClick.bind(this)),
    );
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== "ESCAPE") return;

    const openDetailsElement = event.target.closest("details[open]");
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(
          event,
          this.mainDetailsToggle.querySelector("summary"),
        )
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest(".has-submenu");
    const isOpen = detailsElement.hasAttribute("open");
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    function addTrapFocus() {
      trapFocus(
        summaryElement.nextElementSibling,
        detailsElement.querySelector("button"),
      );
      summaryElement.nextElementSibling.removeEventListener(
        "transitionend",
        addTrapFocus,
      );
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen
        ? this.closeMenuDrawer(event, summaryElement)
        : this.openMenuDrawer(summaryElement);

      if (window.matchMedia("(max-width: 990px)")) {
        document.documentElement.style.setProperty(
          "--viewport-height",
          `${window.innerHeight}px`,
        );
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add("menu-opening");
        summaryElement.setAttribute("aria-expanded", true);
        parentMenuElement && parentMenuElement.classList.add("submenu-open");
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener(
              "transitionend",
              addTrapFocus,
            );
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
    });
    summaryElement.setAttribute("aria-expanded", true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove("menu-opening");
    this.mainDetailsToggle.querySelectorAll("details").forEach((details) => {
      details.removeAttribute("open");
      details.classList.remove("menu-opening");
    });
    this.mainDetailsToggle
      .querySelectorAll(".submenu-open")
      .forEach((submenu) => {
        submenu.classList.remove("submenu-open");
      });
    document.body.classList.remove(
      `overflow-hidden-${this.dataset.breakpoint}`,
    );
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent)
      elementToFocus?.setAttribute("aria-expanded", false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (
        this.mainDetailsToggle.hasAttribute("open") &&
        !this.mainDetailsToggle.contains(document.activeElement)
      )
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest("details");
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest(".submenu-open");
    parentMenuElement && parentMenuElement.classList.remove("submenu-open");
    detailsElement.classList.remove("menu-opening");
    detailsElement
      .querySelector("summary")
      .setAttribute("aria-expanded", false);
    removeTrapFocus(detailsElement.querySelector("summary"));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute("open");
        if (detailsElement.closest("details[open]")) {
          trapFocus(
            detailsElement.closest("details[open]"),
            detailsElement.querySelector("summary"),
          );
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define("menu-drawer", MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector(".section-header");
    this.borderOffset =
      this.borderOffset ||
      this.closest(".header-wrapper").classList.contains(
        "header-wrapper--border-bottom",
      )
        ? 1
        : 0;
    document.documentElement.style.setProperty(
      "--header-bottom-position",
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`,
    );
    this.header.classList.add("menu-open");

    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
    });

    summaryElement.setAttribute("aria-expanded", true);
    window.addEventListener("resize", this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove("menu-open");
    window.removeEventListener("resize", this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        "--header-bottom-position",
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`,
      );
    document.documentElement.style.setProperty(
      "--viewport-height",
      `${window.innerHeight}px`,
    );
  };
}

customElements.define("header-drawer", HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener(
      "click",
      this.hide.bind(this, false),
    );
    this.addEventListener("keyup", (event) => {
      if (event.code.toUpperCase() === "ESCAPE") this.hide();
    });
    if (this.classList.contains("media-modal")) {
      this.addEventListener("pointerup", (event) => {
        if (
          event.pointerType === "mouse" &&
          !event.target.closest("deferred-media, product-model")
        )
          this.hide();
      });
    } else {
      this.addEventListener("click", (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest(".shopify-section").id.replace(
      "shopify-section-",
      "",
    );
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector(".template-popup");
    document.body.classList.add("overflow-hidden");
    this.setAttribute("open", "");
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove("overflow-hidden");
    document.body.dispatchEvent(new CustomEvent("modalClosed"));
    this.removeAttribute("open");
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define("modal-dialog", ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === "") {
        const productUrl = this.dataset.url.split("?")[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html",
            );
            const sourceQty = html.querySelector(
              ".quick-order-list-container",
            ).parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(
        `#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`,
      ),
    );
  }
}

customElements.define("bulk-modal", BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector("button");

    if (!button) return;
    button.addEventListener("click", () => {
      const modal = document.querySelector(this.getAttribute("data-modal"));
      if (modal) modal.show(button);
    });
  }
}
customElements.define("modal-opener", ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener("click", this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute("loaded")) {
      const content = document.createElement("div");
      content.appendChild(
        this.querySelector("template").content.firstElementChild.cloneNode(
          true,
        ),
      );

      this.setAttribute("loaded", true);
      const deferredElement = this.appendChild(
        content.querySelector("video, model-viewer, iframe"),
      );
      if (focus) deferredElement.focus();
      if (
        deferredElement.nodeName == "VIDEO" &&
        deferredElement.getAttribute("autoplay")
      ) {
        // force autoplay for safari
        deferredElement.play();
      }
    }
  }
}

customElements.define("deferred-media", DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector(".slider-counter--current");
    this.pageTotalElement = this.querySelector(".slider-counter--total");
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener("scroll", this.update.bind(this));
    this.prevButton.addEventListener("click", this.onButtonClick.bind(this));
    this.nextButton.addEventListener("click", this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter(
      (element) => element.clientWidth > 0,
    );
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset =
      this.sliderItemsToShow[1].offsetLeft -
      this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) /
        this.sliderItemOffset,
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage =
      Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent("slideChanged", {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        }),
      );
    }

    if (this.enableSliderLooping) return;

    if (
      this.isSlideVisible(this.sliderItemsToShow[0]) &&
      this.slider.scrollLeft === 0
    ) {
      this.prevButton.setAttribute("disabled", "disabled");
    } else {
      this.prevButton.removeAttribute("disabled");
    }

    if (
      this.isSlideVisible(
        this.sliderItemsToShow[this.sliderItemsToShow.length - 1],
      )
    ) {
      this.nextButton.setAttribute("disabled", "disabled");
    } else {
      this.nextButton.removeAttribute("disabled");
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide =
      this.slider.clientWidth + this.slider.scrollLeft - offset;
    return (
      element.offsetLeft + element.clientWidth <= lastVisibleSlide &&
      element.offsetLeft >= this.slider.scrollLeft
    );
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === "next"
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define("slider-component", SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector(".slider-buttons");
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector(".slideshow__slide");
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector(".announcement-bar-slider");
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(
      this.sliderControlWrapper.querySelectorAll(".slider-counter__link"),
    );
    this.sliderControlLinksArray.forEach((link) =>
      link.addEventListener("click", this.linkToSlide.bind(this)),
    );
    this.slider.addEventListener("scroll", this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      );
      this.reducedMotion.addEventListener("change", () => {
        if (this.slider.getAttribute("data-autoplay") === "true")
          this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          "click",
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true },
        );
      });
    }

    if (this.slider.getAttribute("data-autoplay") === "true")
      this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener("mouseover", this.focusInHandling.bind(this));
    this.addEventListener("mouseleave", this.focusOutHandling.bind(this));
    this.addEventListener("focusin", this.focusInHandling.bind(this));
    this.addEventListener("focusout", this.focusOutHandling.bind(this));

    if (this.querySelector(".slideshow__autoplay")) {
      this.sliderAutoplayButton = this.querySelector(".slideshow__autoplay");
      this.sliderAutoplayButton.addEventListener(
        "click",
        this.autoPlayToggle.bind(this),
      );
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked
        ? this.pause()
        : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === "previous") {
      this.slideScrollPosition =
        this.slider.scrollLeft +
        this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === "next") {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll(".slider-counter__link");
    this.prevButton.removeAttribute("disabled");

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove("slider-counter__link--active");
      link.removeAttribute("aria-current");
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add(
      "slider-counter__link--active",
    );
    this.sliderControlButtons[this.currentPage - 1].setAttribute(
      "aria-current",
      true,
    );
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton ||
        this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (
      !this.reducedMotion.matches &&
      !this.announcementBarArrowButtonWasClicked
    ) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton ||
        this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute("aria-live", "off");
    clearInterval(this.autoplay);
    this.autoplay = setInterval(
      this.autoRotateSlides.bind(this),
      this.autoplaySpeed,
    );
  }

  pause() {
    this.slider.setAttribute("aria-live", "polite");
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add("slideshow__autoplay--paused");
      this.sliderAutoplayButton.setAttribute(
        "aria-label",
        window.accessibilityStrings.playSlideshow,
      );
    } else {
      this.sliderAutoplayButton.classList.remove("slideshow__autoplay--paused");
      this.sliderAutoplayButton.setAttribute(
        "aria-label",
        window.accessibilityStrings.pauseSlideshow,
      );
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length
        ? 0
        : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll("a");
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute("tabindex");
          });
        item.setAttribute("aria-hidden", "false");
        item.removeAttribute("tabindex");
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute("tabindex", "-1");
          });
        item.setAttribute("aria-hidden", "true");
        item.setAttribute("tabindex", "-1");
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = "next") {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === "next" ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = "announcement-bar-slider--fade-in";
    const animationClassOut = "announcement-bar-slider--fade-out";

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext =
      (button === "next" && !isLastSlide) ||
      (button === "previous" && isFirstSlide);
    const direction = shouldMoveNext ? "next" : "previous";

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) +
          1 -
          this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define("slideshow-component", SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener("change", (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === "SELECT" && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute("selected"))
        .removeAttribute("selected");
      target.selectedOptions[0].setAttribute("selected", "selected");

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest(".product-form__input")
        .querySelector("[data-selected-value] > .swatch");
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty(
          "--swatch--background",
          swatchValue,
        );
        selectedDropdownSwatchValue.classList.remove("swatch--unavailable");
      } else {
        selectedDropdownSwatchValue.style.setProperty(
          "--swatch--background",
          "unset",
        );
        selectedDropdownSwatchValue.classList.add("swatch--unavailable");
      }

      selectedDropdownSwatchValue.style.setProperty(
        "--swatch-focal-point",
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || "unset",
      );
    } else if (tagName === "INPUT" && target.type === "radio") {
      const selectedSwatchValue = target
        .closest(`.product-form__input`)
        .querySelector("[data-selected-value]");
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === "SELECT" ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(
      this.querySelectorAll("select option[selected], fieldset input:checked"),
    ).map(({ dataset }) => dataset.optionValueId);
  }
}

customElements.define("variant-selects", VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: "0px 0px 400px 0px" },
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(
      `${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`,
    )
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement("div");
        html.innerHTML = text;
        const recommendations = html.querySelector("product-recommendations");

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (
          !this.querySelector("slideshow-component") &&
          this.classList.contains("complementary-products")
        ) {
          this.remove();
        }

        if (html.querySelector(".grid__item")) {
          this.classList.add("product-recommendations--loaded");
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define("product-recommendations", ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector(".icon");
  }

  connectedCallback() {
    document.addEventListener(
      "storefront:signincompleted",
      this.handleStorefrontSignInCompleted.bind(this),
    );
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define("account-icon", AccountIcon);

class BulkAdd extends HTMLElement {
  constructor() {
    super();
    this.queue = [];
    this.requestStarted = false;
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });
    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, 250);
  }

  sendRequest(queue) {
    this.requestStarted = true;
    const items = {};
    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter(
      (queueElement) => !queue.includes(queueElement),
    );
    const quickBulkElement =
      this.closest("quick-order-list") || this.closest("quick-add-bulk");
    quickBulkElement.updateMultipleQty(items);
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute("value");
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(
        event,
        index,
        window.quickOrderListStrings.min_error.replace(
          "[min]",
          event.target.dataset.min,
        ),
      );
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(
        event,
        index,
        window.quickOrderListStrings.max_error.replace(
          "[max]",
          event.target.max,
        ),
      );
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(
        event,
        index,
        window.quickOrderListStrings.step_error.replace(
          "[step]",
          event.target.step,
        ),
      );
    } else {
      event.target.setCustomValidity("");
      event.target.reportValidity();
      this.startQueue(index, inputValue);
    }
  }

  getSectionsUrl() {
    if (window.pageNumber) {
      return `${window.location.pathname}?page=${window.pageNumber}`;
    } else {
      return `${window.location.pathname}`;
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }
}

if (!customElements.get("bulk-add")) {
  customElements.define("bulk-add", BulkAdd);
}

/* ---------------------------------------------------------------------------
   Hub-based product filtering & opt-out UI
   - Detect user's location (cached), find nearest hub, and redirect to
     /collections/all/<hub_handle> when within hub radius.
   - Shows a small opt-out banner allowing users to revert to the full collection.
   - Configure HUBS below with your hub handles and coordinates.
--------------------------------------------------------------------------- */
(function () {
  // Configuration: fetch hubs from backend; fallback to empty array if fetch fails.
  let HUBS = [];

  async function fetchHubsFromBackend(
    url = "https://admin.easybiznus.com/api/shopify/hubs?category=Footwear&active=true",
    timeout = 30000,
  ) {
    try {
      console.log("fetchHubsFromBackend: fetching", url);
      const controller = new AbortController();
      const id = setTimeout(() => controller.abort(), timeout);
      let resp;
      try {
        resp = await fetch(url, { signal: controller.signal });
      } catch (fetchErr) {
        clearTimeout(id);
        if (fetchErr && fetchErr.name === "AbortError") {
          console.error("fetchHubsFromBackend: request aborted (timeout)");
        } else {
          console.error(
            "fetchHubsFromBackend: fetch failed (network/CORS?):",
            fetchErr,
          );
        }
        return null;
      }
      clearTimeout(id);

      console.log(
        "fetchHubsFromBackend: response status",
        resp.status,
        resp.statusText,
      );

      const contentType =
        resp.headers.get("content-type") || "(no content-type)";
      let text;
      try {
        text = await resp.text();
      } catch (e) {
        console.error("fetchHubsFromBackend: error reading response text", e);
        return null;
      }

      console.log("fetchHubsFromBackend: response content-type", contentType);
      console.log("fetchHubsFromBackend: raw response text:", text);

      if (!resp.ok) {
        console.error("Failed to fetch hubs:", resp.status, resp.statusText);
        return null;
      }

      let data;
      try {
        data = JSON.parse(text);
      } catch (e) {
        console.error("fetchHubsFromBackend: JSON parse error", e);
        return null;
      }

      console.log("got hubs data", data);
      // Support several backend response shapes. Some backends wrap the
      // array in an object (e.g. { data: [...] }) and use different field
      // names (slug, latitude, longitude, service_radius_km). Normalize
      // to an array of { handle, name, lat, lon, radiusMeters }.
      let rawArray = null;
      if (Array.isArray(data)) rawArray = data;
      else if (data && Array.isArray(data.data)) rawArray = data.data;
      else if (data && Array.isArray(data.hubs)) rawArray = data.hubs;

      if (!rawArray) return null;

      const mapped = rawArray
        .map((h) => {
          if (!h) return null;
          const handle = h.handle || h.slug || h.name || h.id || null;
          const name = h.name || h.category_name || handle || "";
          const latRaw = h.lat ?? h.latitude ?? h.latitude_deg ?? null;
          const lonRaw = h.lon ?? h.longitude ?? h.longitude_deg ?? null;
          const radiusRaw =
            h.radiusMeters ?? h.service_radius_km ?? h.radius_km ?? null;

          const lat = latRaw != null ? parseFloat(latRaw) : NaN;
          const lon = lonRaw != null ? parseFloat(lonRaw) : NaN;

          // Determine radius in meters.
          // If the data came from h.radiusMeters it's already in meters.
          // If it came from h.service_radius_km or h.radius_km it's in km.
          const radiusFieldIsMeters = h.radiusMeters != null;
          let radiusMeters = 0;
          if (radiusRaw == null) {
            radiusMeters = 0;
          } else {
            const n =
              typeof radiusRaw === "string" ? parseFloat(radiusRaw) : radiusRaw;
            radiusMeters = Number.isFinite(n)
              ? Math.round(radiusFieldIsMeters ? n : n * 1000)
              : 0;
          }
          console.log(
            `Hub "${name}": radiusRaw=${radiusRaw}, radiusFieldIsMeters=${radiusFieldIsMeters}, radiusMeters=${radiusMeters}`,
          );

          return { handle, name, lat, lon, radiusMeters };
        })
        .filter(
          (h) => h && h.handle && !Number.isNaN(h.lat) && !Number.isNaN(h.lon),
        );

      return mapped;
    } catch (e) {
      console.error("Error fetching hubs (unexpected):", e);
      return null;
    }
  }

  const LOCATION_STORAGE_KEY = "hubUserLocation";
  const SELECTED_HUB_KEY = "selectedHub";
  const HUB_OPTOUT_KEY = "hubOptedOutToAll"; // set when user explicitly picks "All Products"
  const HUB_REDIRECT_DONE_KEY = "hubAutoRedirectDone"; // one-shot guard – prevents redirect loops
  const LOCATION_TTL_MS = 1000 * 60 * 60; // 1 hour

  // Cookie-based redirect guard – survives cross-origin Shopify domain redirects
  function setRedirectGuardCookie() {
    document.cookie = "hub_rd=1;path=/;max-age=120;SameSite=Lax";
  }
  function hasRedirectGuardCookie() {
    return document.cookie.includes("hub_rd=1");
  }
  function clearRedirectGuardCookie() {
    document.cookie = "hub_rd=;path=/;max-age=0;SameSite=Lax";
  }

  function toRad(deg) {
    return (deg * Math.PI) / 180;
  }

  function distanceMeters(lat1, lon1, lat2, lon2) {
    const R = 6371000;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) *
        Math.cos(toRad(lat2)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    console.log(`Distance to hub: ${R * c} meters`);
    return R * c;
  }

  function getCachedLocation() {
    try {
      const raw = sessionStorage.getItem(LOCATION_STORAGE_KEY);
      if (!raw) return null;
      const obj = JSON.parse(raw);
      if (!obj.ts) return null;
      if (Date.now() - obj.ts > LOCATION_TTL_MS) {
        sessionStorage.removeItem(LOCATION_STORAGE_KEY);
        return null;
      }
      return obj;
    } catch (e) {
      return null;
    }
  }

  function cacheLocation(loc) {
    try {
      sessionStorage.setItem(
        LOCATION_STORAGE_KEY,
        JSON.stringify({ ...loc, ts: Date.now() }),
      );
    } catch (e) {}
  }

  async function ipFallback() {
    try {
      const resp = await fetch("https://ipapi.co/json/");
      if (!resp.ok) return null;
      const j = await resp.json();
      if (j && j.latitude && j.longitude) {
        const loc = {
          lat: parseFloat(j.latitude),
          lon: parseFloat(j.longitude),
          accuracy: 50000,
        };
        cacheLocation(loc);
        return loc;
      }
    } catch (e) {}
    return null;
  }

  function obtainLocationOnce({ timeout = 10000 } = {}) {
    return new Promise((resolve) => {
      const cached = getCachedLocation();
      if (cached) return resolve(cached);
      if (!navigator.geolocation) {
        ipFallback().then(resolve);
        return;
      }
      let settled = false;
      const success = (position) => {
        if (settled) return;
        settled = true;
        const loc = {
          lat: position.coords.latitude,
          lon: position.coords.longitude,
          accuracy: position.coords.accuracy || null,
        };
        cacheLocation(loc);
        resolve(loc);
      };
      const error = () => {
        if (settled) return;
        settled = true;
        ipFallback().then(resolve);
      };
      try {
        navigator.geolocation.getCurrentPosition(success, error, {
          enableHighAccuracy: true,
          timeout,
          maximumAge: 0,
        });
      } catch (e) {
        error();
      }
    });
  }

  function findNearestHub(lat, lon) {
    let best = null;
    console.log("All HUBS:", HUBS);
    for (const hub of HUBS) {
      const d = distanceMeters(lat, lon, hub.lat, hub.lon);
      console.log(
        `Hub "${hub.name}": distance=${Math.round(d)}m, radius=${hub.radiusMeters}m, inRange=${d <= hub.radiusMeters}`,
      );
      if (!best || d < best.distance) best = { hub, distance: d };
    }
    if (best) {
      console.log(
        `Nearest hub: "${best.hub.name}" at ${Math.round(best.distance)}m (radius: ${best.hub.radiusMeters}m, inRange: ${best.distance <= best.hub.radiusMeters})`,
      );
    }
    return best;
  }

  // Shimmer overlay helpers
  function showShimmerOverlay() {
    if (document.getElementById("hub-shimmer-overlay")) return;
    const styleId = "hub-shimmer-styles";
    if (!document.getElementById(styleId)) {
      const s = document.createElement("style");
      s.id = styleId;
      s.innerHTML = `
        @keyframes hub-shimmer {
          0% { background-position: -200% 0 }
          100% { background-position: 200% 0 }
        }
        #hub-shimmer-overlay { position:fixed; inset:0; background: rgba(0, 0, 0, 0.63); z-index:999999; display:flex; align-items:center; justify-content:center; }
        .hub-shimmer-card { width:90%; max-width:1100px; display:grid; grid-template-columns: repeat(auto-fill, minmax(180px,1fr)); gap:16px; }
        .hub-shimmer-item { height:260px; border-radius:8px; overflow:hidden; background:linear-gradient(90deg,#f0f0f0 25%, #e6e6e6 50%, #f0f0f0 75%); background-size: 200% 100%; animation: hub-shimmer 1.2s linear infinite; }
        .hub-shimmer-loader { position:absolute; top:50%; left:50%; transform:translate(-50%, -50%); display:flex; align-items:center; justify-content:center; background: transparent; padding:0; border-radius:0; z-index:1000000; }
        .hub-shimmer-spinner { width:48px; height:48px; display:block; }
      `;
      document.head.appendChild(s);
    }
    const overlay = document.createElement("div");
    overlay.id = "hub-shimmer-overlay";
    const container = document.createElement("div");
    container.className = "hub-shimmer-card";
    for (let i = 0; i < 8; i++) {
      const it = document.createElement("div");
      it.className = "hub-shimmer-item";
      container.appendChild(it);
    }
    overlay.appendChild(container);

    const loader = document.createElement("div");
    loader.className = "hub-shimmer-loader";
    try {
      console.log("showShimmerOverlay: showing overlay");
    } catch (e) {}
    loader.innerHTML =
      '<svg class="hub-shimmer-spinner" role="status" aria-label="Loading" viewBox="0 0 50 50" xmlns="http://www.w3.org/2000/svg"><circle cx="25" cy="25" r="20" fill="none" stroke="rgba(255,255,255,0.18)" stroke-width="6"></circle><path d="M45 25a20 20 0 0 0-20-20" fill="none" stroke="#fff" stroke-width="6" stroke-linecap="round"><animateTransform attributeName="transform" type="rotate" dur="1s" from="0 25 25" to="360 25 25" repeatCount="indefinite"/></path></svg>';
    overlay.appendChild(loader);

    document.body.appendChild(overlay);
  }

  function removeShimmerOverlay() {
    const el = document.getElementById("hub-shimmer-overlay");
    if (el) el.remove();
    const style = document.getElementById("hub-shimmer-styles");
    if (style) style.remove();
  }

  function isHubCollectionPath(path) {
    return /\/collections\/(all\/)?hub[-_]/.test(path);
  }

  // ---------------------------------------------------------------------------
  // Hub source toggle – injected into filter/facets area on collection pages
  // Lets user switch between "Nearby Hub" products and "All Products".
  // ---------------------------------------------------------------------------
  function injectHubSourceToggle() {
    // Only show on /collections/all* pages
    const path = window.location.pathname;
    if (!/^\/collections\/all(?:$|\/)/.test(path)) return;

    const isOnHub = isHubCollectionPath(path);
    const selectedHub = sessionStorage.getItem(SELECTED_HUB_KEY);
    const hubObj = selectedHub
      ? HUBS.find((h) => h.handle === selectedHub)
      : null;
    const hubLabel = hubObj
      ? hubObj.name.replace(/^hub_/i, "").replace(/_/g, " ")
      : "Nearby Products";

    // Detect if we were bounced from a hub URL by Shopify
    const wasBounced =
      !isOnHub &&
      selectedHub &&
      sessionStorage.getItem(HUB_REDIRECT_DONE_KEY) === "true";
    // Nearby tab should be active if we're on a hub URL OR if we bounced
    const nearbyActive = isOnHub || wasBounced;

    // Inject styles once
    if (!document.getElementById("hub-toggle-styles")) {
      const style = document.createElement("style");
      style.id = "hub-toggle-styles";
      style.textContent = `
        .hub-source-toggle {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 0 14px;
          flex-wrap: wrap;
          font-family: inherit;
        }
        .hub-source-toggle__label {
          font-size: 13px;
          font-weight: 600;
          color: #333;
          margin-right: 4px;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }
        .hub-source-toggle__btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 7px 16px;
          border-radius: 999px;
          border: 1.5px solid #ccc;
          background: #fff;
          color: #555;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          line-height: 1.3;
          white-space: nowrap;
        }
        .hub-source-toggle__btn:hover {
          border-color: #888;
          color: #222;
        }
        .hub-source-toggle__btn--active {
          background: #222;
          color: #fff;
          border-color: #222;
        }
        .hub-source-toggle__btn--active:hover {
          background: #333;
          border-color: #333;
          color: #fff;
        }
        .hub-source-toggle__btn svg {
          width: 14px;
          height: 14px;
          flex-shrink: 0;
        }
        .hub-source-toggle__btn--loading {
          opacity: 0.7;
          pointer-events: none;
        }
        .hub-source-toggle__btn--loading svg.hub-spinner {
          animation: hub-btn-spin 0.8s linear infinite;
        }
        .hub-nearby-empty-state {
          display: none;
          width: 100%;
          max-width: 72rem;
          margin: 2.8rem auto 2rem;
          padding: 0 1.5rem;
        }
        .hub-nearby-empty-state__content {
          display: flex;
          flex-direction: column;
          align-items: center;
          width: 100%;
          padding: 3.4rem 3.2rem 3rem;
          border: 1px solid #dbe4ef;
          border-radius: 24px;
          background:
            linear-gradient(180deg, #ffffff 0%, #fbfcfe 100%);
          font-family: var(--font-body-family, inherit);
          text-align: center;
          position: relative;
          overflow: hidden;
          box-shadow:
            0 24px 58px rgba(15, 23, 42, 0.1),
            0 8px 18px rgba(23, 37, 84, 0.06);
        }
        .hub-nearby-empty-state__content::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 11rem;
          background:
            linear-gradient(180deg, #f7f9fc 0%, rgba(247, 249, 252, 0) 100%);
          pointer-events: none;
        }
        .hub-nearby-empty-state__visual {
          position: relative;
          display: flex;
          justify-content: center;
          align-items: flex-end;
          gap: 1rem;
          width: 23.5rem;
          max-width: 100%;
          min-height: 11.2rem;
          margin: 0 auto 2.1rem;
          z-index: 1;
        }
        .hub-nearby-empty-state__product-card {
          width: 6.6rem;
          height: 8.6rem;
          margin: 0;
          padding: 0.7rem;
          border-radius: 8px;
          border: 1px solid #dbe4ef;
          background:
            linear-gradient(180deg, #ffffff 0%, #fbfcfe 100%);
          box-shadow:
            0 10px 22px rgba(23, 37, 84, 0.08),
            inset 0 1px 0 rgba(255, 255, 255, 0.9);
        }
        .hub-nearby-empty-state__product-card:nth-child(1) {
          transform: rotate(-5deg) translateY(0.8rem);
        }
        .hub-nearby-empty-state__product-card:nth-child(3) {
          transform: rotate(5deg) translateY(0.8rem);
        }
        .hub-nearby-empty-state__product-image {
          display: block;
          width: 100%;
          height: 4.8rem;
          border-radius: 7px;
          background:
            radial-gradient(circle at 50% 54%, rgba(71, 85, 105, 0.12) 0 1.1rem, transparent 1.15rem),
            linear-gradient(180deg, #ffffff 0%, #f3f7fb 100%);
          border: 1px solid #e7edf5;
        }
        .hub-nearby-empty-state__product-line {
          display: block;
          height: 0.45rem;
          margin-top: 0.8rem;
          border-radius: 999px;
          background: #cbd7e6;
        }
        .hub-nearby-empty-state__product-line--short {
          width: 62%;
          margin-top: 0.5rem;
          background: #e7edf5;
        }
        .hub-nearby-empty-state__pin {
          position: absolute;
          left: 50%;
          top: 1.8rem;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 5.4rem;
          height: 5.4rem;
          border: 1px solid #dbe4ef;
          border-radius: 50%;
          background:
            linear-gradient(180deg, #ffffff 0%, #f7f9fc 100%);
          color: #0f172a;
          box-shadow:
            0 16px 30px rgba(15, 23, 42, 0.14),
            inset 0 1px 0 rgba(255, 255, 255, 0.95);
          transform: translateX(-50%);
        }
        .hub-nearby-empty-state__pin svg {
          width: 2.4rem;
          height: 2.4rem;
        }
        .hub-nearby-empty-state__copy {
          min-width: 0;
        }
        .hub-nearby-empty-state__label {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-height: 2.4rem;
          margin: 0 0 1.1rem;
          padding: 0.2rem 1.1rem;
          border: 1px solid #dbe4ef;
          border-radius: 999px;
          background: #edf3fb;
          font-size: 0.92rem;
          font-weight: 700;
          line-height: 1;
          letter-spacing: 0.06em;
          color: #4b6480;
          text-transform: uppercase;
        }
        .hub-nearby-empty-state__title {
          margin: 0 0 0.7rem;
          font-family: var(--font-body-family, inherit);
          font-style: var(--font-body-style, normal);
          font-size: 2.35rem;
          font-weight: 700;
          color: #111827;
          letter-spacing: 0;
          line-height: 1.18;
        }
        .hub-nearby-empty-state__text {
          margin: 0;
          font-size: 1.22rem;
          font-weight: 400;
          color: #64748b;
          line-height: 1.48;
          max-width: 42rem;
          margin-left: auto;
          margin-right: auto;
        }
        .hub-nearby-empty-state__actions {
          display: flex;
          justify-content: center;
          margin: 2rem 0 0;
        }
        .hub-nearby-empty-state__btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.55rem;
          min-width: 0;
          min-height: 3.8rem;
          padding: 0 1.6rem;
          border: 1px solid #0f172a;
          border-radius: 999px;
          background: #0f172a;
          color: #ffffff;
          font-family: var(--font-body-family, inherit);
          font-size: 1.18rem;
          font-weight: 700;
          cursor: pointer;
          text-decoration: none;
          transition:
            background 0.18s ease,
            border-color 0.18s ease,
            box-shadow 0.18s ease,
            transform 0.18s ease;
          letter-spacing: 0;
          white-space: nowrap;
          box-shadow:
            0 10px 18px rgba(15, 23, 42, 0.16),
            inset 0 1px 0 rgba(255, 255, 255, 0.12);
        }
        .hub-nearby-empty-state__btn:hover {
          background: #1e293b;
          border-color: #1e293b;
          transform: translateY(-0.1rem);
          box-shadow:
            0 18px 30px rgba(15, 23, 42, 0.22),
            inset 0 1px 0 rgba(255, 255, 255, 0.14);
        }
        .hub-nearby-empty-state__btn svg {
          width: 1.25rem;
          height: 1.25rem;
          flex-shrink: 0;
        }
        @media screen and (max-width: 749px) {
          .hub-nearby-empty-state {
            padding: 0;
            margin: 2rem 0 1.2rem;
          }
          .hub-nearby-empty-state__content {
            padding: 2.8rem 1.8rem 2.2rem;
            border-radius: 20px;
          }
          .hub-nearby-empty-state__visual {
            width: 18.8rem;
            min-height: 9.8rem;
            margin-bottom: 1.8rem;
          }
          .hub-nearby-empty-state__product-card {
            width: 5.4rem;
            height: 7.4rem;
          }
          .hub-nearby-empty-state__product-image {
            height: 4.2rem;
          }
          .hub-nearby-empty-state__pin {
            width: 4.8rem;
            height: 4.8rem;
            top: 1.6rem;
          }
          .hub-nearby-empty-state__label {
            min-height: 2.2rem;
            font-size: 0.86rem;
          }
          .hub-nearby-empty-state__title {
            font-size: 1.9rem;
            line-height: 1.2;
          }
          .hub-nearby-empty-state__text {
            font-size: 1.14rem;
            line-height: 1.45;
          }
          .hub-nearby-empty-state__actions {
            justify-content: center;
            margin-top: 2rem;
          }
          .hub-nearby-empty-state__btn {
            width: auto;
            min-width: 0;
            min-height: 3.7rem;
            padding: 0 1.5rem;
          }
        }
        @keyframes hub-btn-spin {
          to { transform: rotate(360deg); }
        }
      `;
      document.head.appendChild(style);
    }

    // Build toggle container
    const toggle = document.createElement("div");
    toggle.className = "hub-source-toggle";
    toggle.id = "hub-source-toggle";

    const locationIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>`;
    const gridIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>`;
    const spinnerSvg = `<svg class="hub-spinner" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" xmlns="http://www.w3.org/2000/svg"><path d="M12 2a10 10 0 0 1 10 10" /></svg>`;

    toggle.innerHTML = `
      <span class="hub-source-toggle__label">Source:</span>
      <button class="hub-source-toggle__btn ${nearbyActive ? "hub-source-toggle__btn--active" : ""}" id="hub-toggle-nearby" type="button">
        ${locationIcon}
        ${hubObj ? hubLabel : "Nearby Products"}
      </button>
      <button class="hub-source-toggle__btn ${!nearbyActive ? "hub-source-toggle__btn--active" : ""}" id="hub-toggle-all" type="button">
        ${gridIcon}
        All Products
      </button>
    `;

    // Find an anchor to inject: above the facets wrapper or the product grid container
    const facetsContainer = document.querySelector(".facets-container");
    const filtersAside = document.getElementById("main-collection-filters");
    const productGridContainer =
      document.getElementById("ProductGridContainer") ||
      document.querySelector('[id^="ProductGridContainer"]');

    if (facetsContainer) {
      facetsContainer.parentNode.insertBefore(toggle, facetsContainer);
    } else if (filtersAside) {
      filtersAside.parentNode.insertBefore(toggle, filtersAside);
    } else if (productGridContainer) {
      productGridContainer.parentNode.insertBefore(
        toggle,
        productGridContainer,
      );
    } else {
      // last resort: prepend to main or body
      const main = document.querySelector("main") || document.body;
      main.prepend(toggle);
    }

    // --- Event handlers ---
    const btnNearby = document.getElementById("hub-toggle-nearby");
    const btnAll = document.getElementById("hub-toggle-all");
    const productGrid =
      document.getElementById("ProductGridContainer") ||
      document.querySelector('[id^="ProductGridContainer"]');

    const setActiveSource = (source) => {
      const nearbyActive = source === "nearby";
      btnNearby.classList.toggle(
        "hub-source-toggle__btn--active",
        nearbyActive,
      );
      btnAll.classList.toggle("hub-source-toggle__btn--active", !nearbyActive);
    };

    const ensureNearbyEmptyState = () => {
      if (!productGrid) return null;
      let state = document.getElementById("hub-nearby-empty-state");
      if (state) return state;

      state = document.createElement("div");
      state.id = "hub-nearby-empty-state";
      state.className = "hub-nearby-empty-state";
      state.innerHTML = `
        <div class="hub-nearby-empty-state__content">
          <div class="hub-nearby-empty-state__visual" aria-hidden="true">
            <span class="hub-nearby-empty-state__product-card">
              <span class="hub-nearby-empty-state__product-image"></span>
              <span class="hub-nearby-empty-state__product-line"></span>
              <span class="hub-nearby-empty-state__product-line hub-nearby-empty-state__product-line--short"></span>
            </span>
            <span class="hub-nearby-empty-state__product-card">
              <span class="hub-nearby-empty-state__product-image"></span>
              <span class="hub-nearby-empty-state__product-line"></span>
              <span class="hub-nearby-empty-state__product-line hub-nearby-empty-state__product-line--short"></span>
            </span>
            <span class="hub-nearby-empty-state__product-card">
              <span class="hub-nearby-empty-state__product-image"></span>
              <span class="hub-nearby-empty-state__product-line"></span>
              <span class="hub-nearby-empty-state__product-line hub-nearby-empty-state__product-line--short"></span>
            </span>
            <span class="hub-nearby-empty-state__pin">
              ${locationIcon}
            </span>
          </div>
          <div class="hub-nearby-empty-state__copy">
            <span class="hub-nearby-empty-state__label">Hub filter active</span>
            <h3 class="hub-nearby-empty-state__title">No products match this hub</h3>
            <p class="hub-nearby-empty-state__text">Products are filtered by location, and this location does not have matching items right now. Browse all products to continue shopping.</p>
          </div>
          <div class="hub-nearby-empty-state__actions">
            <button type="button" class="hub-nearby-empty-state__btn" id="hub-empty-show-all-btn">
              ${gridIcon}
              Show all products
            </button>
          </div>
        </div>
      `;
      productGrid.appendChild(state);

      const showAllFromState = state.querySelector("#hub-empty-show-all-btn");
      if (showAllFromState) {
        showAllFromState.addEventListener("click", () => {
          // Clear all hub state
          sessionStorage.removeItem(SELECTED_HUB_KEY);
          sessionStorage.removeItem(HUB_REDIRECT_DONE_KEY);
          clearRedirectGuardCookie();
          sessionStorage.setItem(HUB_OPTOUT_KEY, "true");

          if (isOnHub) {
            // On a hub URL — navigate to /collections/all
            showShimmerOverlay();
            window.location.href = "/collections/all";
          } else {
            // Already on /collections/all (bounced from hub) — just toggle UI
            showAllProductsState();
          }
        });
      }
      return state;
    };

    const showNearbyEmptyState = () => {
      if (!productGrid) return;
      Array.from(productGrid.children).forEach((child) => {
        if (child.id !== "hub-nearby-empty-state") child.style.display = "none";
      });
      // Also hide all filter / facet UI
      document
        .querySelectorAll(
          "#main-collection-filters, facet-filters-form, .facets-container, .facets-vertical-sort, .active-facets",
        )
        .forEach((el) => {
          el.style.display = "none";
        });
      const state = ensureNearbyEmptyState();
      if (state) state.style.display = "block";
      setActiveSource("nearby");
    };

    const showAllProductsState = () => {
      if (!productGrid) return;
      Array.from(productGrid.children).forEach((child) => {
        if (child.id === "hub-nearby-empty-state") {
          child.style.display = "none";
        } else {
          child.style.display = "";
        }
      });
      // Restore filter / facet UI
      document
        .querySelectorAll(
          "#main-collection-filters, facet-filters-form, .facets-container, .facets-vertical-sort, .active-facets",
        )
        .forEach((el) => {
          el.style.display = "";
        });
      setActiveSource("all");
    };

    btnNearby.addEventListener("click", async () => {
      if (isOnHub) return; // already on hub
      // Clear opt-out flags; set fresh state for hub attempt
      sessionStorage.removeItem(HUB_OPTOUT_KEY);
      sessionStorage.removeItem(HUB_REDIRECT_DONE_KEY);
      clearRedirectGuardCookie();
      setActiveSource("nearby");
      // Show loading state
      btnNearby.classList.add("hub-source-toggle__btn--loading");
      btnNearby.innerHTML = `${spinnerSvg} Finding products...`;
      showShimmerOverlay();

      try {
        const loc = await obtainLocationOnce({ timeout: 10000 });
        if (loc && loc.lat && loc.lon) {
          const nearest = findNearestHub(loc.lat, loc.lon);
          if (nearest && nearest.distance <= nearest.hub.radiusMeters) {
            sessionStorage.setItem(SELECTED_HUB_KEY, nearest.hub.handle);
            // Set guards BEFORE navigating – if Shopify bounces the hub URL
            // back to /collections/all, the auto-redirect won't fire again.
            sessionStorage.setItem(HUB_REDIRECT_DONE_KEY, "true");
            setRedirectGuardCookie();
            window.location.href = `/collections/all/${nearest.hub.handle}`;
            return;
          }
        }
        // No nearby hub found
        removeShimmerOverlay();
        btnNearby.classList.remove("hub-source-toggle__btn--loading");
        btnNearby.innerHTML = `${locationIcon} Nearby Products`;
        showNearbyEmptyState();
      } catch (err) {
        console.error("Error finding nearby products:", err);
        removeShimmerOverlay();
        btnNearby.classList.remove("hub-source-toggle__btn--loading");
        btnNearby.innerHTML = `${locationIcon} Nearby Products`;
        showNearbyEmptyState();
      }
    });

    btnAll.addEventListener("click", () => {
      if (!isOnHub && !sessionStorage.getItem(SELECTED_HUB_KEY)) {
        // Already on /collections/all with no hub context – just show all
        showAllProductsState();
        return;
      }
      showShimmerOverlay();
      sessionStorage.removeItem(SELECTED_HUB_KEY);
      sessionStorage.removeItem(HUB_REDIRECT_DONE_KEY);
      clearRedirectGuardCookie();
      sessionStorage.setItem(HUB_OPTOUT_KEY, "true"); // prevent auto-redirect back to hub
      if (isOnHub) {
        window.location.href = "/collections/all";
      } else {
        // Already on /collections/all but showing empty state after a hub bounce
        removeShimmerOverlay();
        showAllProductsState();
      }
    });

    // ── On-load: detect hub state and show appropriate UI ──────────────
    if (isOnHub && productGrid) {
      // We successfully landed on a hub URL – check if Shopify actually
      // filtered products (tag exists) or not.
      const productsCount = parseInt(productGrid.dataset.productsCount, 10);
      const allProductsCount = parseInt(
        productGrid.dataset.allProductsCount,
        10,
      );
      const hasEmptyClass = !!productGrid.querySelector(".collection--empty");

      const tagIsInvalid =
        hasEmptyClass ||
        productsCount === 0 ||
        (!isNaN(productsCount) &&
          !isNaN(allProductsCount) &&
          productsCount === allProductsCount);

      if (tagIsInvalid) {
        showNearbyEmptyState();
      }
    } else if (!isOnHub && productGrid) {
      // We're on /collections/all – check if we were BOUNCED here from a
      // hub URL (Shopify stripped the tag because it doesn't exist).
      const bouncedFromHub =
        sessionStorage.getItem(SELECTED_HUB_KEY) &&
        sessionStorage.getItem(HUB_REDIRECT_DONE_KEY) === "true";

      if (bouncedFromHub) {
        // User tried to reach a hub but Shopify bounced us.
        // Show the empty state so the user knows and can click "Show all".
        console.log("Detected hub bounce – showing nearby empty state");
        setActiveSource("nearby");
        showNearbyEmptyState();
      }
    }
  }

  function showOptOutBanner(hub) {
    try {
      if (document.getElementById("hub-optout-banner")) return;
      const banner = document.createElement("div");
      banner.id = "hub-optout-banner";
      banner.style.position = "fixed";
      banner.style.bottom = "16px";
      banner.style.left = "16px";
      banner.style.right = "16px";
      banner.style.zIndex = 99999;
      banner.style.background = "rgba(0,0,0,0.85)";
      banner.style.color = "#fff";
      banner.style.padding = "12px 16px";
      banner.style.borderRadius = "6px";
      banner.style.display = "flex";
      banner.style.alignItems = "center";
      banner.style.justifyContent = "space-between";
      banner.style.boxSizing = "border-box";
      banner.innerHTML = `
        <div style="flex:1; font-size:14px">Showing products from <strong>${hub.name}</strong> (nearest hub). <button id="hub-show-all" style="margin-left:8px; padding:6px 10px; border-radius:4px; border:none; cursor:pointer;">Show all products</button></div>
        <div style="margin-left:12px"><button id="hub-optout-close" style="background:transparent;color:#fff;border:1px solid rgba(255,255,255,0.2);padding:6px 10px;border-radius:4px;cursor:pointer">Close</button></div>
      `;
      document.body.appendChild(banner);

      document
        .getElementById("hub-show-all")
        .addEventListener("click", async () => {
          try {
            showShimmerOverlay();
            sessionStorage.removeItem(LOCATION_STORAGE_KEY);
            sessionStorage.removeItem(SELECTED_HUB_KEY);
            sessionStorage.removeItem(HUB_OPTOUT_KEY);

            const loc = await obtainLocationOnce({ timeout: 10000 });
            if (loc && loc.lat && loc.lon) {
              const nearest = findNearestHub(loc.lat, loc.lon);
              if (nearest && nearest.distance <= nearest.hub.radiusMeters) {
                sessionStorage.setItem(SELECTED_HUB_KEY, nearest.hub.handle);
                window.location.href = `/collections/all/${nearest.hub.handle}`;
                return;
              }
            }
            window.location.href = "/collections/all";
          } catch (e) {
            console.error(
              "Error while resolving location on Show all click",
              e,
            );
            removeShimmerOverlay();
            window.location.href = "/collections/all";
          }
        });
      document
        .getElementById("hub-optout-close")
        .addEventListener("click", () => {
          banner.remove();
        });
    } catch (e) {
      console.error("Could not show opt-out banner", e);
    }
  }

  async function maybeRedirectToHubCollection() {
    try {
      const path = window.location.pathname;

      // ── GUARD 1: Only auto-redirect from EXACTLY /collections/all ──
      // Any sub-path like /collections/all/hub_xxx will NOT match,
      // so even if Shopify bounces us back, we can't loop.
      const isExactAllCollection =
        path === "/collections/all" || path === "/collections/all/";
      const isSearch = /^\/search(?:$|\/)/.test(path);

      if (!isExactAllCollection && !isSearch) return false;

      // ── GUARD 2: Session flag ── already auto-redirected once this session
      if (sessionStorage.getItem(HUB_REDIRECT_DONE_KEY) === "true") {
        console.log("Hub redirect skipped: already redirected this session");
        return false;
      }

      // ── GUARD 3: Cookie flag ── survives cross-origin Shopify domain hops
      if (hasRedirectGuardCookie()) {
        console.log("Hub redirect skipped: cookie guard");
        return false;
      }

      // ── GUARD 4: User opted out to "All Products" ──
      if (sessionStorage.getItem(HUB_OPTOUT_KEY) === "true") {
        console.log("Hub redirect skipped: user opted out to All Products");
        return false;
      }

      // Show shimmer so user doesn't see a flash of unfiltered products
      showShimmerOverlay();

      const loc = await obtainLocationOnce();
      if (!loc || !loc.lat || !loc.lon) {
        removeShimmerOverlay();
        return false;
      }

      const nearest = findNearestHub(loc.lat, loc.lon);
      if (!nearest || nearest.distance > nearest.hub.radiusMeters) {
        removeShimmerOverlay();
        return false;
      }

      const target = `/collections/all/${nearest.hub.handle}`;
      sessionStorage.setItem(SELECTED_HUB_KEY, nearest.hub.handle);

      // ── SET GUARDS BEFORE NAVIGATING so they're in place on the next page ──
      sessionStorage.setItem(HUB_REDIRECT_DONE_KEY, "true");
      setRedirectGuardCookie();

      console.log(
        `Redirecting to nearest hub: ${nearest.hub.name} -> ${target}`,
      );
      window.location.href = target;
      return true; // redirect initiated
    } catch (e) {
      console.error("Hub redirect error", e);
      removeShimmerOverlay();
      return false;
    }
  }

  document.addEventListener("DOMContentLoaded", async () => {
    try {
      const fetched = await fetchHubsFromBackend();
      if (fetched && fetched.length) {
        HUBS = fetched;
        console.log("Loaded HUBS from backend:", HUBS);
      } else {
        HUBS = [];
        console.warn("No hubs loaded; proceeding without hub redirects.");
      }
    } catch (e) {
      HUBS = [];
      console.error("Error loading hubs, proceeding without hub redirects", e);
    }

    // ── Attempt auto-redirect to nearest hub (awaited to avoid race) ──
    const redirected = await maybeRedirectToHubCollection();
    if (redirected) return; // page is navigating away, skip the rest

    // Inject the hub source toggle into the filter area (if on a collection page)
    injectHubSourceToggle();

    // Delegated click handler: intercept clicks to /collections/all links so we can
    // show shimmer and request location before navigating.
    document.addEventListener("click", async (e) => {
      try {
        if (e.defaultPrevented) return;
        if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey)
          return;
        const a = e.target.closest && e.target.closest("a[href]");
        if (!a) return;
        const href = a.getAttribute("href");
        if (!href) return;
        let url;
        try {
          url = new URL(href, window.location.origin);
        } catch (err) {
          return;
        }
        if (!url.pathname.startsWith("/collections/all")) return;
        if (a.target && a.target.toLowerCase() === "_blank") return;

        // Don't intercept intra-page navigation (pagination, sorting, filtering)
        // so that ?page=2, ?sort_by=..., etc. work correctly.
        const currentPath = window.location.pathname.replace(/\/$/, "");
        const targetPath = url.pathname.replace(/\/$/, "");
        if (currentPath === targetPath) return;

        e.preventDefault();

        showShimmerOverlay();
        sessionStorage.removeItem(LOCATION_STORAGE_KEY);
        sessionStorage.removeItem(SELECTED_HUB_KEY);
        sessionStorage.removeItem(HUB_OPTOUT_KEY);
        sessionStorage.removeItem(HUB_REDIRECT_DONE_KEY);
        clearRedirectGuardCookie();

        try {
          const loc = await obtainLocationOnce({ timeout: 10000 });
          if (loc && loc.lat && loc.lon) {
            const nearest = findNearestHub(loc.lat, loc.lon);
            if (nearest && nearest.distance <= nearest.hub.radiusMeters) {
              sessionStorage.setItem(SELECTED_HUB_KEY, nearest.hub.handle);
              window.location.href = `/collections/all/${nearest.hub.handle}`;
              return;
            }
          }
        } catch (err) {
          console.error(
            "Error obtaining location for /collections/all click",
            err,
          );
        }
        window.location.href = "/collections/all";
      } catch (e) {
        console.error("Error handling delegated /collections/all click", e);
      }
    });
  });
})();
